/**
 * Automation Module - Messages Automation Query Services
 * Migrated from src/services/useAutomation.ts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  GET_ONBOARDING_URL,
  GET_ALL_AUTOMATIONS_URL,
  GET_LISTING_MANAGEMENT_URL,
  GET_AUTOMATION_BY_ID_URL,
  GET_MASTER_DATA_URL,
  GET_UNSYNCED_PROPERTIES_URL,
  LIST_NABEEH_SERVICES_URL,
  LIST_NABEEH_SERVICE_CATEGORIES_URL,
  LIST_CHECKLIST_ITEMS_URL,
  CREATE_CHECKLIST_ITEM_URL,
  DELETE_CHECKLIST_ITEM_URL,
  LIST_SERVICE_PROVIDERS_URL,
  CREATE_TASK_AUTOMATION_URL,
  UPDATE_TASK_AUTOMATION_URL,
  GET_TASK_AUTOMATION_BY_ID_URL,
} from '../constants';
import type { IListServiceProvidersResponse, IListNabeehServicesResponse, IListNabeehServiceCategoriesResponse, ICreateChecklistItemRequest, ICreateTaskAutomationRequest, ICreateTaskAutomationResponse, IUpdateTaskAutomationRequest, IGetAutomationByIdResponse, IGetTaskAutomationByIdResponse } from '../types/tasks.types';

export const useGetOnboarding = () => {
  const { apiPrivate } = useApi();

  return useQuery({
    enabled: false,
    queryKey: [GET_ONBOARDING_URL],
    queryFn: async () => {
      const { data } = await apiPrivate.get(GET_ONBOARDING_URL);
      return data;
    },
  });
};

export const useGetAllAutomations = (params: {
  pageSize: string;
  pageNumber: string;
  search?: string;
}) => {
  const { apiPrivate } = useApi();

  return useQuery({
    enabled: true,
    queryKey: [GET_ALL_AUTOMATIONS_URL, params],
    queryFn: async () => {
      const { data } = await apiPrivate.get(
        `${GET_ALL_AUTOMATIONS_URL}?pageSize=${params.pageSize}&pageNumber=${params.pageNumber}`
      );
      return data;
    },
  });
};

export const useGetListingManagement = (params?: {
  pageSize?: number;
  pageNumber?: number;
}) => {
  const { apiPrivate } = useApi();
  const pageSize = params?.pageSize || 10;
  const pageNumber = params?.pageNumber || 1;

  return useQuery({
    enabled: true,
    queryKey: [GET_LISTING_MANAGEMENT_URL, pageNumber, pageSize],
    queryFn: async () => {
      const { data } = await apiPrivate.get(
        `${GET_LISTING_MANAGEMENT_URL}?pageSize=${pageSize}&pageNumber=${pageNumber}`
      );
      return data;
    },
  });
};

export const useGetAutomationById = (params: { id: number }, options?: { enabled?: boolean }) => {
  const { apiPrivate } = useApi();

  return useQuery<IGetAutomationByIdResponse>({
    enabled: options?.enabled !== false,
    queryKey: [GET_AUTOMATION_BY_ID_URL, params.id],
    queryFn: async () => {
      const { data } = await apiPrivate.get(
        `${GET_AUTOMATION_BY_ID_URL}?Id=${params.id}`
      );
      return data;
    },
  });
};

export const useGetMasterData = (key?: string) => {
  const { apiPrivate } = useApi();

  return useQuery({
    enabled: true,
    queryKey: [GET_MASTER_DATA_URL, key],
    queryFn: async () => {
      const { data } = await apiPrivate.get(GET_MASTER_DATA_URL, { params: { key } });
      return data;
    },
  });
};

export const useGetUnSyncedProperties = () => {
  const { apiPrivate } = useApi();

  return useQuery({
    enabled: true,
    queryKey: [GET_UNSYNCED_PROPERTIES_URL],
    queryFn: async () => {
      const { data } = await apiPrivate.get(GET_UNSYNCED_PROPERTIES_URL);
      return data;
    },
  });
};

export const useListNabeehServices = () => {
  const { apiPrivate } = useApi();

  return useQuery<IListNabeehServicesResponse>({
    queryKey: [LIST_NABEEH_SERVICES_URL],
    queryFn: async () => {
      const { data } = await apiPrivate.get(LIST_NABEEH_SERVICES_URL);
      return data;
    },
  });
};

export const useListNabeehServiceCategories = () => {
  const { apiPrivate } = useApi();

  return useQuery<IListNabeehServiceCategoriesResponse>({
    queryKey: [LIST_NABEEH_SERVICE_CATEGORIES_URL],
    queryFn: async () => {
      const { data } = await apiPrivate.get(LIST_NABEEH_SERVICE_CATEGORIES_URL);
      return data;
    },
  });
};

export const useListChecklistItems = (serviceId?: string) => {
  const { apiPrivate } = useApi();

  return useQuery({
    enabled: !!serviceId,
    queryKey: [LIST_CHECKLIST_ITEMS_URL, serviceId],
    queryFn: async () => {
      const { data } = await apiPrivate.get(LIST_CHECKLIST_ITEMS_URL, { params: { serviceId } });
      return data;
    },
  });
};

export const useCreateChecklistItem = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, ICreateChecklistItemRequest>({
    mutationFn: async (request) => {
      const { data } = await apiPrivate.post(
        `${CREATE_CHECKLIST_ITEM_URL}?serviceId=${request.serviceId}`,
        request.body,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [LIST_CHECKLIST_ITEMS_URL, variables.serviceId] });
    },
  });
};

export const useDeleteChecklistItem = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { id: number; serviceId: string }>({
    mutationFn: async ({ id, serviceId }) => {
      const { data } = await apiPrivate.delete(
        `${DELETE_CHECKLIST_ITEM_URL}/${id}`,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [LIST_CHECKLIST_ITEMS_URL, variables.serviceId] });
    },
  });
};

// TODO: Replace hardcoded tenantId when backend provides current user tenant endpoint
const HARDCODED_TENANT_ID = '019c957e-44cd-719d-a927-cf9ed26b9744';

export const useListServiceProviders = () => {
  const { apiPrivate } = useApi();

  return useQuery<IListServiceProvidersResponse>({
    queryKey: [LIST_SERVICE_PROVIDERS_URL, HARDCODED_TENANT_ID],
    queryFn: async () => {
      const { data } = await apiPrivate.get(LIST_SERVICE_PROVIDERS_URL, {
        params: { tenantId: HARDCODED_TENANT_ID },
      });
      return data;
    },
  });
};

export const useGetTaskAutomationById = (params: { id: number }, options?: { enabled?: boolean }) => {
  const { apiPrivate } = useApi();

  return useQuery<IGetTaskAutomationByIdResponse>({
    enabled: options?.enabled !== false,
    queryKey: [GET_TASK_AUTOMATION_BY_ID_URL, params.id],
    queryFn: async () => {
      const { data } = await apiPrivate.get(
        `${GET_TASK_AUTOMATION_BY_ID_URL}?Id=${params.id}`
      );
      return data;
    },
  });
};

export const useCreateTaskAutomation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<ICreateTaskAutomationResponse, Error, ICreateTaskAutomationRequest>({
    mutationFn: async (request) => {
      const { data } = await apiPrivate.post(CREATE_TASK_AUTOMATION_URL, request);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_ALL_AUTOMATIONS_URL] });
    },
  });
};

export const useUpdateTaskAutomation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  return useMutation<ICreateTaskAutomationResponse, Error, IUpdateTaskAutomationRequest>({
    mutationFn: async (request) => {
      const { data } = await apiPrivate.post(
        `${UPDATE_TASK_AUTOMATION_URL}?Id=${request.id}`,
        request.body,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [GET_ALL_AUTOMATIONS_URL] });
      queryClient.invalidateQueries({ queryKey: [GET_TASK_AUTOMATION_BY_ID_URL, variables.id] });
    },
  });
};
